import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser'
import { listOrgs, listRds, type ResearchDomain } from '../services/authApi'
import { usePersona } from '../PersonaContext'

/**
 * RD-Admin landing. In the RD-Admin preview, a customer admin doesn't think in
 * "orgs" — they administer one (or a few) Research Domains. This page lets you
 * pick which RD to act as admin of; selecting one sets the persona's activeRdId
 * and drops you into that RD's management screens.
 *
 * In real Ros, an RD Admin's RD list comes from their memberships (the RDs where
 * their role is owner/admin). Here we list all RDs so you can preview any of them.
 */
export function RdAdminHome() {
  const navigate = useNavigate()
  const { setActiveRdId } = usePersona()
  const [rds, setRds] = useState<ResearchDomain[]>([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    (async () => {
      try {
        const orgs = await listOrgs()
        const all: ResearchDomain[] = []
        for (const org of orgs) {
          const orgRds = await listRds(org.id).catch(() => [])
          all.push(...orgRds)
        }
        setRds(all)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load research domains')
      } finally {
        setLoading(false)
      }
    })()
  }, [])

  function pick(rd: ResearchDomain) {
    setActiveRdId(rd.id)
    navigate(`/rds/${rd.id}`)
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <VerifiedUserIcon color="primary" />
        <Typography variant="h3">Your Research Domains</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        RD-Admin preview. Pick a Research Domain to manage its members, roles, policies, groups, agents, settings, and audit log.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        In production, an RD Admin signs into Ros and sees only the RDs they administer (from their memberships). This preview lists all RDs so you can explore any of them. Org/RD creation and the global registries stay Platform-Admin-only.
      </Alert>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Typography color="text.secondary">Loading…</Typography>
      ) : rds.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'divider' }} elevation={0}>
          <Typography color="text.secondary">No Research Domains found. Create one in Platform Admin view first.</Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 2 }}>
          {rds.map(rd => (
            <Paper
              key={rd.id}
              onClick={() => pick(rd)}
              sx={{
                p: 2.5, border: '1px solid', borderColor: 'divider', cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
              elevation={0}
            >
              <Typography variant="body1" sx={{ fontWeight: 600 }}>{rd.name}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{rd.slug}</Typography>
              <Box sx={{ mt: 1 }}>
                <Chip size="small" label={`${rd.memberCount ?? 0} members`} variant="outlined" />
                {rd.isArchived ? <Chip size="small" label="archived" color="warning" variant="outlined" sx={{ ml: 0.5 }} /> : null}
              </Box>
            </Paper>
          ))}
        </Box>
      )}
    </Box>
  )
}
